import { useCallback, useEffect, useState, type RefObject } from 'react';

/**
 * Fullscreen for one element. Safari on iPhone defines no element Fullscreen
 * API at all, so a caller that speaks only the standard one leaves its button
 * dead; without the API the element is pinned over the visible viewport
 * instead. `active` covers both routes, so callers hold one layout state.
 */
export const useFullscreen = (ref: RefObject<HTMLElement | null>) => {
  const [nativeActive, setNativeActive] = useState(false);
  const [pinned, setPinned] = useState(false);

  useEffect(() => {
    const onChange = () =>
      // Guard the null===null case: an unmounted ref must not read as
      // "still fullscreen" just because nothing is fullscreen.
      setNativeActive(
        document.fullscreenElement != null && document.fullscreenElement === ref.current,
      );
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, [ref]);

  useEffect(() => {
    const el = ref.current;
    if (!el || !pinned) return;
    el.dataset.pseudoFullscreen = 'true';
    document.documentElement.dataset.pseudoFullscreenLock = 'true';
    return () => {
      delete el.dataset.pseudoFullscreen;
      delete document.documentElement.dataset.pseudoFullscreenLock;
    };
  }, [pinned, ref]);

  const toggle = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof el.requestFullscreen !== 'function') {
      setPinned((on) => !on);
      return;
    }
    if (document.fullscreenElement === el) {
      void Promise.resolve(document.exitFullscreen?.()).catch(() => undefined);
    } else {
      void Promise.resolve(el.requestFullscreen()).catch(() => undefined);
    }
  }, [ref]);

  return { active: nativeActive || pinned, toggle };
};
